/**
 * Outbox — Offline-Sync-Queue in IndexedDB (ADR-0005, Phase 1).
 */

import { getElkheirDb } from './elkheirDb';
import { kvGet, kvRemove } from './kvAccess';
import { normalizeBusinessStorageId } from '../../utils/sharedCore';

const LEGACY_REPORTS_QUEUE_KEY = 'offlineReportsQueue';

/** IndexedDB kann keine zirkulären / nicht-klonbaren Werte speichern. */
function cloneForIndexedDb(value) {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (e) {
    console.warn('[Outbox] payload clone failed:', e?.message || e);
    return null;
  }
}

/** Stable business_id for sale/purchase outbox payloads (strip business_ prefix). */
export function normalizeOutboxBusinessId(businessId) {
  return normalizeBusinessStorageId(businessId) || String(businessId || '');
}

/**
 * Local-only sync results that should finish the row (done).
 * env_off only — plan_none stays deferred until admin enables Mirror/Hybrid.
 */
export function isPermanentOutboxLocalOnly(reason) {
  return String(reason || '') === 'env_off';
}

/**
 * ADR-0017 §3.2 — stable outbox ids (bootstrap + live mutation MUST match).
 * Sale void re-puts the same id with the final sale payload.
 */
export function saleOutboxId(sale, mutationId = null) {
  const raw =
    mutationId ||
    sale?.clientMutationId ||
    sale?.id ||
    sale?.orderId ||
    null;
  const id = String(raw ?? '').trim();
  return id || null;
}

export function purchaseOutboxId(purchase, mutationId = null) {
  const raw =
    mutationId ||
    purchase?.idempotency_key ||
    purchase?.purchaseId ||
    purchase?.id ||
    null;
  const id = String(raw ?? '').trim();
  return id || null;
}

/**
 * @param {string} businessId
 * @param {object} order
 * @param {'upsert'|'delete'} [op]
 */
export function orderOutboxId(businessId, order, op = 'upsert') {
  const biz = normalizeOutboxBusinessId(businessId || order?.businessId || order?.business_id);
  const orderKey = String(
    order?.orderId || order?.order_id || order?.id || ''
  ).trim();
  if (!biz || !orderKey) return null;
  return `order_${biz}_${orderKey}${op === 'delete' ? '_del' : ''}`;
}

/** Build sale_sync outbox entry (shared by persist + bootstrap). */
export function buildSaleSyncOutboxEntry(businessId, sale, mutationId = null) {
  const mid = saleOutboxId(sale, mutationId);
  if (!mid || !sale) return null;
  const biz = normalizeOutboxBusinessId(businessId);
  return {
    id: mid,
    entityType: 'sale_sync',
    payload: {
      businessId: biz,
      sale,
      clientMutationId: mid
    }
  };
}

/** Build purchase_sync outbox entry (shared by repo + bootstrap + void). */
export function buildPurchaseSyncOutboxEntry(businessId, purchase, mutationId = null) {
  const mid = purchaseOutboxId(purchase, mutationId);
  if (!mid || !purchase) return null;
  const biz = normalizeOutboxBusinessId(businessId);
  return {
    id: mid,
    entityType: 'purchase_sync',
    payload: {
      businessId: biz,
      purchase,
      clientMutationId: mid
    }
  };
}

/** Build order_sync outbox entry. */
export function buildOrderSyncOutboxEntry(businessId, order, op = 'upsert') {
  const mid = orderOutboxId(businessId, order, op);
  if (!mid || !order) return null;
  const biz = normalizeOutboxBusinessId(businessId || order.businessId || order.business_id);
  const orderKey = String(order.orderId || order.order_id || order.id || '').trim();
  return {
    id: mid,
    entityType: 'order_sync',
    payload: {
      businessId: biz,
      order: { ...order, orderId: orderKey, businessId: biz },
      op: op === 'delete' ? 'delete' : 'upsert',
      clientMutationId: `order_${orderKey}`
    }
  };
}

/** movementId is the stable identity (ADR-0016 / ADR-0017 W4). */
export function inventoryMovementOutboxId(movement) {
  const id = String(movement?.movementId || movement?.id || '').trim();
  return id || null;
}

/** Build inventory_movement_sync outbox entry (append-only remote). */
export function buildInventoryMovementSyncOutboxEntry(businessId, movement) {
  const mid = inventoryMovementOutboxId(movement);
  if (!mid || !movement) return null;
  const biz = normalizeOutboxBusinessId(businessId);
  return {
    id: `inv_mov_${biz}_${mid}`,
    entityType: 'inventory_movement_sync',
    payload: {
      businessId: biz,
      movement,
      clientMutationId: mid
    }
  };
}

/**
 * Dexie-Schema v2 (outbox) muss geöffnet sein — sonst DexieError bei put.
 */
export async function ensureOutboxReady() {
  const db = getElkheirDb();
  if (!db.isOpen()) {
    await db.open();
  }
  if (!db.outbox) {
    throw new Error(
      'IndexedDB outbox table missing — clear elkheir_v1 or reload after schema upgrade'
    );
  }
  return db;
}

function generateMutationId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `mut_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * @param {{ entityType: string, payload: unknown, id?: string }} entry
 */
export function buildOutboxRow(entry) {
  const id = String(entry.id || generateMutationId());
  const payload = cloneForIndexedDb(entry.payload);
  if (payload == null) {
    throw new Error('buildOutboxRow: payload is not JSON-serializable');
  }
  return {
    id,
    entityType: String(entry.entityType || 'unknown'),
    payload,
    status: 'pending',
    createdAt: new Date().toISOString(),
    attempts: 0,
    lastError: null,
    processedAt: null
  };
}

/**
 * @param {{ entityType: string, payload: unknown, id?: string }} entry
 */
export async function enqueueOutbox(entry) {
  const db = await ensureOutboxReady();
  const row = buildOutboxRow(entry);
  await db.outbox.put(row);
  return row;
}

export async function listPendingOutbox(entityType = null, limit = 500) {
  const db = getElkheirDb();
  let query = db.outbox.where('status').equals('pending');
  const rows = await query.toArray();
  const filtered = entityType
    ? rows.filter((r) => r.entityType === entityType)
    : rows;
  return filtered
    .sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)))
    .slice(0, limit);
}

export async function markOutboxProcessing(id) {
  const db = getElkheirDb();
  const row = await db.outbox.get(id);
  if (!row) return false;
  await db.outbox.put({ ...row, status: 'processing' });
  return true;
}

export async function markOutboxDone(id) {
  const db = getElkheirDb();
  const row = await db.outbox.get(id);
  if (!row) return false;
  await db.outbox.put({
    ...row,
    status: 'done',
    processedAt: new Date().toISOString()
  });
  return true;
}

export async function markOutboxFailed(id, errorMessage) {
  const db = getElkheirDb();
  const row = await db.outbox.get(id);
  if (!row) return false;
  await db.outbox.put({
    ...row,
    status: 'failed',
    attempts: (row.attempts || 0) + 1,
    lastError: errorMessage || 'unknown',
    processedAt: new Date().toISOString()
  });
  return true;
}

/** Zurück auf pending — Retry bei Offline / localOnly / unterbrochenem Drain. */
export async function markOutboxPending(id, errorMessage = null) {
  const db = getElkheirDb();
  const row = await db.outbox.get(id);
  if (!row) return false;
  await db.outbox.put({
    ...row,
    status: 'pending',
    lastError: errorMessage ?? row.lastError ?? null,
    processedAt: null
  });
  return true;
}

export async function resetFailedOutboxToPending(entityType = null) {
  const db = getElkheirDb();
  const failed = await db.outbox.where('status').equals('failed').toArray();
  const targets = entityType
    ? failed.filter((r) => r.entityType === entityType)
    : failed;
  await Promise.all(
    targets.map((row) =>
      db.outbox.put({
        ...row,
        status: 'pending',
        lastError: null,
        processedAt: null
      })
    )
  );
  return targets.length;
}

/**
 * Vor Sync-Drain: failed + hängende processing-Einträge wieder pending.
 * @param {string|null} [entityType]
 */
export async function recoverOutboxForRetry(entityType = null) {
  const db = getElkheirDb();
  const stuck = await db.outbox
    .where('status')
    .anyOf(['failed', 'processing'])
    .toArray();
  const targets = entityType
    ? stuck.filter((r) => r.entityType === entityType)
    : stuck;
  await Promise.all(
    targets.map((row) =>
      db.outbox.put({
        ...row,
        status: 'pending',
        lastError: row.status === 'failed' ? row.lastError : null,
        processedAt: null
      })
    )
  );
  return targets.length;
}

/** Einmalig: KV-Queue `offlineReportsQueue` → Outbox. */
export async function migrateLegacyOfflineReportsQueue() {
  const stored = kvGet(LEGACY_REPORTS_QUEUE_KEY);
  if (!stored || !Array.isArray(stored) || stored.length === 0) {
    return { migrated: 0 };
  }

  let migrated = 0;
  for (const item of stored) {
    if (!item?.businessId || !item?.reportData) continue;
    await enqueueOutbox({
      entityType: 'report_sync',
      payload: {
        businessId: item.businessId,
        reportData: item.reportData,
        timestamp: item.timestamp || new Date().toISOString()
      }
    });
    migrated++;
  }

  if (migrated > 0) {
    kvRemove(LEGACY_REPORTS_QUEUE_KEY);
    console.info(`[Outbox] Migrated ${migrated} legacy report(s) from KV → IndexedDB outbox`);
  }

  return { migrated };
}

export async function getOutboxStats() {
  const db = getElkheirDb();
  const all = await db.outbox.toArray();
  return {
    pending: all.filter((r) => r.status === 'pending').length,
    processing: all.filter((r) => r.status === 'processing').length,
    failed: all.filter((r) => r.status === 'failed').length,
    done: all.filter((r) => r.status === 'done').length
  };
}

/**
 * ADR-0017 W6 — manager/settings health: status counts + entity breakdown + sample errors.
 */
export async function getOutboxHealthSummary() {
  const db = await ensureOutboxReady();
  const all = await db.outbox.toArray();
  const byStatus = {
    pending: 0,
    processing: 0,
    failed: 0,
    done: 0,
    other: 0
  };
  const byEntityType = {};
  let deferred = 0;
  const recentErrors = [];

  for (const row of all) {
    const st = String(row.status || 'other');
    if (byStatus[st] != null) byStatus[st]++;
    else byStatus.other++;

    const et = String(row.entityType || 'unknown');
    if (!byEntityType[et]) {
      byEntityType[et] = { pending: 0, failed: 0, processing: 0, done: 0, deferred: 0 };
    }
    if (byEntityType[et][st] != null) byEntityType[et][st]++;
    else byEntityType[et].pending += st === 'pending' ? 1 : 0;

    const err = String(row.lastError || '');
    if (st === 'pending' && err.startsWith('deferred')) {
      deferred++;
      byEntityType[et].deferred = (byEntityType[et].deferred || 0) + 1;
    }
    if (st === 'failed' && err && recentErrors.length < 8) {
      recentErrors.push({
        id: row.id,
        entityType: et,
        lastError: err,
        attempts: row.attempts || 0
      });
    }
  }

  const waiting = byStatus.pending + byStatus.processing + deferred;
  return {
    total: all.length,
    ...byStatus,
    deferred,
    waiting,
    byEntityType,
    recentErrors
  };
}
